import { Spinner } from '@consigli/facade';
import {
  useNotifyPublishedDocumentPackageMutation,
  usePackage,
  usePackageId,
  useProjectId,
  useUpdatePackageMutation,
} from '@consigli/hooks';
import { Button } from '@consigli/shadcn';
import { useCallback } from 'react';
import { HiOutlineEye, HiOutlineEyeOff } from 'react-icons/hi';

import { errorToast, successToast } from '@/components/toast';

/**
 * Button to toggle a package's published status.
 * When a package is unpublished, it shows a "Publish" button.
 * When a package is published, it shows an "Unpublish" button.
 * Automatically sends an email notification to the package uploader on publish.
 */
export const PackageVisibilityButton = () => {
  const projectId = useProjectId();
  const packageId = usePackageId();
  const { pkg } = usePackage(projectId, packageId);
  const [updatePackage, { isLoading: isUpdating }] = useUpdatePackageMutation();
  const [notifyPublished] = useNotifyPublishedDocumentPackageMutation();

  const handleToggleVisibility = useCallback(async () => {
    if (!projectId || !packageId || !pkg) return;

    const published = !pkg.published;

    try {
      await updatePackage({
        projectId,
        packageId,
        data: { published },
        isHidden: pkg.isHidden,
      }).unwrap();
      successToast({
        message: published ? 'Package is now published' : 'Package is now unpublished',
      });

      if (published) {
        try {
          await notifyPublished({
            projectId,
            packageId,
            language: pkg.language ?? 'en',
            emailRecipient: '',
            message: '',
          }).unwrap();
          successToast({ message: 'Notification email sent to package uploader' });
        } catch (error) {
          console.error('Failed to send publish notification email:', error);
          errorToast({ message: 'Failed to send publish notification email' });
        }
      }
    } catch (error) {
      console.error('Failed to update package publish status:', error);
      errorToast({ message: 'Failed to update package publish status' });
    }
  }, [projectId, packageId, pkg, updatePackage, notifyPublished]);

  if (!pkg) {
    return null;
  }

  return pkg.published ? (
    <Button variant="destructive" onClick={handleToggleVisibility} disabled={isUpdating}>
      {isUpdating ? <Spinner size="xsmall" /> : <HiOutlineEyeOff />}
      {isUpdating ? 'Updating...' : 'Unpublish'}
    </Button>
  ) : (
    <Button variant="default" onClick={handleToggleVisibility} disabled={isUpdating}>
      {isUpdating ? <Spinner size="xsmall" /> : <HiOutlineEye />}
      {isUpdating ? 'Updating...' : 'Publish'}
    </Button>
  );
};
